import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRightLeft, Check, Eye, FilePlus2, MoreHorizontal, Pencil, Unlink } from "lucide-react";
import { useMoveThreadNote, useThread, useThreads } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { Thread, ThreadAction, ThreadNoteEntry } from "@/api/types";
import { cn, formatDateTime, timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { ClientBadge } from "@/components/ClientBadge";
import { threadTitle } from "@/components/ThreadCard";
import { ErrorState, EmptyState } from "@/components/States";
import { PageSkeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/** Normalise the detail payload: `actions[]` if present, else `notes[]` carrying action/at. */
export function threadEntries(t: Thread): ThreadNoteEntry[] {
  const titles = new Map<string, string | null>();
  for (const n of t.notes ?? []) titles.set(n.id, n.title);
  let entries: ThreadNoteEntry[] = [];
  if (t.actions && t.actions.length > 0) {
    entries = t.actions.map((a) => ({ ...a, title: a.title ?? titles.get(a.noteId) ?? null }));
  } else {
    // The API nests per-note actions: notes[].actions[{action, at}].
    entries = (t.notes ?? []).flatMap((n) => {
      if (n.actions && n.actions.length > 0) {
        return n.actions.map((a) => ({ noteId: n.id, title: n.title, action: a.action, at: a.at }));
      }
      return [{ noteId: n.id, title: n.title, action: n.action ?? "patched", at: n.at ?? n.updatedAt }];
    });
  }
  return entries.sort((a, b) => (a.at ?? "").localeCompare(b.at ?? ""));
}

const ACTION: Record<ThreadAction, { label: string; icon: typeof Check; variant: "success" | "info" | "outline" }> = {
  created: { label: "created", icon: FilePlus2, variant: "success" },
  patched: { label: "patched", icon: Pencil, variant: "info" },
  read: { label: "read", icon: Eye, variant: "outline" },
};

export default function ThreadPage() {
  const { id } = useParams<{ id: string }>();
  const thread = useThread(id);
  if (thread.isLoading) return <PageSkeleton />;
  if (thread.isError) return <ErrorState error={thread.error} retry={() => thread.refetch()} />;
  return <ThreadView t={thread.data!} />;
}

function ThreadView({ t }: { t: Thread }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const move = useMoveThreadNote(t.id);
  const others = useThreads(t.spaceId);
  const [moving, setMoving] = useState<{ noteId: string; title: string | null } | null>(null);
  const entries = threadEntries(t);
  // One row per note for the actions; a note can have several entries (created, patched, read).
  const noteIds = [...new Set(entries.map((e) => e.noteId))];

  function doMove(noteId: string, toThreadId: string | null) {
    move.mutate(
      { noteId, toThreadId },
      {
        onSuccess: (r) => {
          setMoving(null);
          toast({ title: toThreadId ? "Note moved" : "Note removed from thread" });
          if (r.sourceDeleted) navigate("/threads", { replace: true });
        },
        onError: (err) => toast({ title: "Could not move note", description: errorMessage(err), tone: "error" }),
      },
    );
  }
  const decisions = Array.isArray(t.decisions) ? t.decisions.map((d) => (typeof d === "string" ? d : JSON.stringify(d))) : [];

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/threads" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Threads
      </Link>
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <ClientBadge client={t.clientId} name={t.clientName} />
          <span className="text-xs text-muted-foreground" title={formatDateTime(t.startedAt)}>
            started {timeAgo(t.startedAt)}
          </span>
          <span className="text-xs text-muted-foreground" title={formatDateTime(t.lastWriteAt)}>
            · last write {timeAgo(t.lastWriteAt)}
          </span>
        </div>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">{threadTitle(t)}</h1>
        {t.summary && <p className="mt-2 text-[0.95rem] leading-relaxed text-muted-foreground">{t.summary}</p>}
        {decisions.length > 0 && (
          <div className="mt-4 rounded-xl border bg-card px-4 py-3">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Decisions</div>
            <ul className="flex flex-col gap-1 text-sm">
              {decisions.map((d, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="mt-1 size-3.5 shrink-0 text-tone-tip" strokeWidth={3} />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes touched</h2>
        {entries.length === 0 ? (
          <EmptyState title="No notes recorded for this thread" />
        ) : (
          <ol className="relative ml-2 border-l pl-6">
            {entries.map((e, i) => {
              const a = ACTION[e.action] ?? ACTION.patched;
              const Icon = a.icon;
              return (
                <li key={`${e.noteId}-${e.at}-${i}`} className="relative pb-5 last:pb-0">
                  <span
                    className={cn(
                      "absolute -left-[31px] top-0.5 flex size-[14px] items-center justify-center rounded-full border-2 bg-card",
                      e.action === "created" ? "border-tone-tip" : e.action === "patched" ? "border-tone-info" : "border-border",
                    )}
                    aria-hidden
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={a.variant}>
                      <Icon /> {a.label}
                    </Badge>
                    <Link to={`/notes/${e.noteId}`} className="text-[0.95rem] font-medium hover:underline">
                      {e.title || "Untitled"}
                    </Link>
                    <span className="text-xs text-muted-foreground" title={formatDateTime(e.at)}>
                      {timeAgo(e.at)}
                    </span>
                    {noteIds.length > 0 && entries.findIndex((x) => x.noteId === e.noteId) === i && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-xs" aria-label={`Actions for ${e.title || "Untitled"}`} className="ml-auto">
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => setMoving({ noteId: e.noteId, title: e.title ?? null })}>
                            <ArrowRightLeft /> Move to another thread…
                          </DropdownMenuItem>
                          <DropdownMenuItem destructive onSelect={() => doMove(e.noteId, null)}>
                            <Unlink /> Remove from this thread
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <Dialog open={!!moving} onOpenChange={(o) => !o && setMoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move “{moving?.title || "Untitled"}” to…</DialogTitle>
            <DialogDescription>Its entries in this thread move to the one you pick. Use this when two conversations were grouped by mistake.</DialogDescription>
          </DialogHeader>
          <ul className="max-h-72 divide-y overflow-y-auto rounded-lg border">
            {(others.data ?? [])
              .filter((o) => o.id !== t.id)
              .map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50 disabled:opacity-50"
                    disabled={move.isPending}
                    onClick={() => moving && doMove(moving.noteId, o.id)}
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{threadTitle(o)}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(o.lastWriteAt)}</span>
                  </button>
                </li>
              ))}
            {others.data && others.data.filter((o) => o.id !== t.id).length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">No other threads in this space.</li>}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoving(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
